import React from "react";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { ChevronRight, Eye, ExternalLink, GraduationCap, ArrowLeft } from "lucide-react";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useEntitlement } from "@/hooks/useEntitlement";
import { useExamContext } from "@/contexts/ExamContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AdminPageHeaderProps {
  title: string;
  section: string;
  userEmail?: string | null;
  onSignOut?: () => void;
  cta?: React.ReactNode;
}

export default function AdminPageHeader({ title, section, userEmail, onSignOut, cta }: AdminPageHeaderProps) {
  const { setViewAsUser } = useImpersonation();
  const { isRealAdmin } = useEntitlement();
  const { availableExams, setSelectedExamSlug } = useExamContext();
  const navigate = useNavigate();

  const enterUserView = (examSlug: string | null, examName: string) => {
    setSelectedExamSlug(examSlug);
    setViewAsUser(true);
    toast.success(
      examSlug
        ? `Viewing app as user — ${examName}`
        : "Viewing app as user — All exams"
    );
    navigate("/dashboard");
  };

  return (
    <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 animate-fade-in">
      <div className="mx-auto w-full px-4 py-3 flex items-center gap-3">
        <SidebarTrigger className="shrink-0" />
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{title}</span>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          <h1 className="text-lg font-semibold text-foreground truncate">{section}</h1>
        </div>
        {cta}
        <div className="flex items-center gap-2">
          {isRealAdmin && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate("/dashboard")}
              title="Return to the user app (stays signed in as admin)"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Back to App</span>
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                title="Preview the app as a regular user for a specific exam"
              >
                <Eye className="mr-2 h-4 w-4" />
                <span className="hidden sm:inline">View as User</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 z-50 bg-background">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Choose an exam to preview
              </DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => enterUserView(null, "All Exams")}
                className="cursor-pointer"
              >
                <GraduationCap className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="flex-1">All Exams</span>
                <a
                  href="/dashboard?exam=all"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title="Open in new tab"
                  className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted text-muted-foreground"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </DropdownMenuItem>
              {availableExams.length > 0 && <DropdownMenuSeparator />}
              {availableExams.map((exam) => (
                <DropdownMenuItem
                  key={exam.slug}
                  onClick={() => enterUserView(exam.slug, exam.name)}
                  className="cursor-pointer"
                >
                  {exam.icon ? (
                    <span className="mr-2 text-base">{exam.icon}</span>
                  ) : (
                    <GraduationCap className="mr-2 h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="flex-1 truncate">{exam.name}</span>
                  <a
                    href={`/dashboard?exam=${encodeURIComponent(exam.slug)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title="Open in new tab"
                    className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted text-muted-foreground"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </DropdownMenuItem>
              ))}
              {availableExams.length === 0 && (
                <DropdownMenuItem disabled>No exams available</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {userEmail && (
            <span className="hidden md:inline text-sm text-muted-foreground max-w-[220px] truncate" title={userEmail}>
              {userEmail}
            </span>
          )}
          {onSignOut && (
            <Button variant="secondary" size="sm" onClick={onSignOut}>
              Sign out
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
